import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { User } from '../types';

interface HeroProps {
  onNavigate: (page: string) => void;
  user?: User | null;
}

const Hero: React.FC<HeroProps> = ({ onNavigate, user }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden flex items-center justify-center">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        {/* Updated Background Photo - Soft Luxury Interior/Texture */}
        <img 
          src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2158&auto=format&fit=crop"
          alt="Luxury Environment Background"
          className="w-full h-full object-cover opacity-100"
        />
        {/* Increased white overlays for a very light, airy feel */}
        <div className="absolute inset-0 bg-stone-50/60 mix-blend-overlay z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-stone-50/95 z-10"></div>
      </div>

      {/* Floating Overlay Photo - Desktop Only - PRESERVED */}
      <div className={`absolute right-[12%] bottom-[15%] z-20 hidden xl:block transition-all duration-1000 delay-500 transform ${loaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
         <div className="relative w-72 aspect-[3/4] shadow-2xl p-3 bg-white/40 backdrop-blur-md border border-white/60 rotate-6 hover:rotate-0 transition-transform duration-700 ease-out cursor-pointer group">
            <img 
              src="https://images.unsplash.com/photo-1603561591411-07134e71a2a9?q=80&w=2000&auto=format&fit=crop" 
              alt="Diamond Ring Feature" 
              className="w-full h-full object-cover" 
            />
            {/* Gloss shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Badge */}
            <div className="absolute -left-5 -bottom-5 w-20 h-20 bg-stone-50 rounded-full flex items-center justify-center shadow-lg border border-stone-200 group-hover:scale-110 transition-transform duration-300">
               <span className="text-[9px] uppercase tracking-widest text-center font-serif leading-3 text-stone-800">
                 Featured<br/>Edit
               </span>
            </div>
         </div>
      </div>

      {/* Content */}
      <div className={`relative z-20 text-center px-4 max-w-4xl mx-auto transition-all duration-1000 transform ${loaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <p className="text-gold-600 text-xs md:text-sm tracking-[0.3em] uppercase mb-6 animate-fade-in font-semibold drop-shadow-sm">
          Sparkling Gems • Philippines
        </p>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-stone-900 mb-8 leading-tight drop-shadow-md">
          Curated Luxury, <br />
          <span className="italic text-gold-600">Authenticated</span>
        </h1>
        <p className="text-stone-900 text-sm md:text-base font-normal tracking-wide max-w-xl mx-auto mb-10 leading-relaxed bg-white/40 backdrop-blur-md p-6 rounded-sm shadow-sm border border-white/40">
          Handpicked bags, jewelry, and fragrances with premium presentation and exclusive member perks. Experience the pinnacle of elegance.
        </p>
        
        <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
          <button 
            onClick={() => onNavigate('shop')}
            className="group relative px-8 py-4 bg-gold-600 text-white overflow-hidden transition-all hover:bg-gold-500 w-48 shadow-lg shadow-gold-200"
          >
            <div className="absolute inset-0 w-0 bg-white/20 transition-all duration-[250ms] ease-out group-hover:w-full"></div>
            <span className="relative z-10 flex items-center justify-center gap-2 text-xs tracking-[0.2em] uppercase">
              Browse Shop <ArrowRight size={14} />
            </span>
          </button>
          
          {!user && (
            <button 
              onClick={() => onNavigate('auth')}
              className="px-8 py-4 border border-stone-800 text-stone-900 hover:border-stone-900 hover:bg-stone-900 hover:text-white transition-all w-48 bg-white/40 backdrop-blur-sm shadow-sm"
            >
              <span className="text-xs tracking-[0.2em] uppercase font-bold">Member Login</span>
            </button>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce text-stone-800">
        <span className="text-[10px] tracking-widest uppercase mb-2 block font-semibold">Scroll</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-stone-800 to-transparent mx-auto"></div>
      </div>
    </section>
  );
};

export default Hero;